import React, { useState } from 'react';
import { Collapse as CollapseClient } from 'reactstrap';
import SvgMessageBubble from 'src/assets/svg/SvgMessageBubble.svg';
import SvgAnaliticsBar from 'src/assets/svg/SvgAnaliticsBar.svg';
import SvgFenceUnsafe from 'src/assets/svg/SvgFenceUnsafe.svg';
import SvgChevronDown from 'src/assets/svg/SvgChevronDown.svg';
import SvgChevronUp from 'src/assets/svg/SvgChevronUp.svg';
import SvgSmallDog from 'src/assets/svg/amenities/small-dog.svg';
import SvgFenced from 'src/assets/svg/SvgFenced.svg';
import SvgInfo from 'src/assets/svg/SvgInfo.svg';
import SvgRule from 'src/assets/svg/SvgRule.svg';
import './index.scss';

const SPOT_ENCLOSURE = {
    FULLY_FENCED: 'fully fenced',
    PARTIALLY_FENCED: 'partially fenced',
    UNFENCED: 'unfenced',
};

const ENCLOSURE_TYPE = {
    FULLY_FENCED: 'FULLY_FENCED',
    PARTIALLY_FENCED: 'PARTIALLY_FENCED',
    UNFENCED: 'UNFENCED',
};

const SPOT_FENCE_HEIGHT = {
    FT_NULL: 'N/A',
    FT_LESS_3: '<3 feet',
    FT_3: '3 feet',
    FT_4: '4 feet',
    FT_5: '5 feet',
    FT_6_PLUS: '6+ feet',
};

const FENCING_TYPES_FOR_EDIT = [
    {
        value: 'WOOD_PRIVACY',
        label: 'Wood - privacy',
    },
    {
        value: 'WOOD_NON_PRIVACY',
        label: 'Wood - non-privacy',
    },
    {
        value: 'CHAIN_LINK_PRIVACY',
        label: 'Chain link - privacy',
    },
    {
        value: 'CHAIN_LINK_NON_PRIVACY',
        label: 'Chain link - non-privacy',
    },
    {
        value: 'CATTLE_WITH_MESH',
        label: 'Cattle with wire mesh',
    },
    {
        value: 'CATTLE_WITHOUT_MESH',
        label: 'Cattle without wire mesh',
    },
];

const Div = ({ children, ...rest }) => <div {...{ rest }}>{children}</div>;

const Enclosure = ({ encls, mobile }) => {
    if (!encls) {
        return '';
    }

    if (mobile) {
        return (
            <div className="enclosure">
                <div className="d-flex">
                    <SvgFenced />
                </div>
                <div>
                    <p className="snif-p mb-1 snif-semibold">This spot is {SPOT_ENCLOSURE[encls.toUpperCase()]}</p>
                    <p className="snif-p mb-3 deep-gray">Info provided by host</p>
                </div>
            </div>
        );
    }

    return (
        <div className="enclosure">
            <SvgFenced />
            <div>
                <p className="snif-p mb-1 snif-semibold">Enclosure</p>
                <p className="snif-p mb-3 deep-gray">{SPOT_ENCLOSURE[encls.toUpperCase()]}</p>
            </div>
        </div>
    );
};

const Fencing = ({ fncHeight, mobile }) => {
    if (!fncHeight) {
        return '';
    }

    if (mobile) {
        return (
            <div className="fence-height">
                <div className="d-flex">
                    <SvgRule />
                </div>
                <div>
                    <p className="snif-p mb-1 snif-semibold">Fencing height</p>
                    <p className="snif-p mb-3 deep-gray">
                        {fncHeight.toUpperCase() != 'FT_NULL'
                            ? `${SPOT_FENCE_HEIGHT[fncHeight.toUpperCase()]} (at lowest point)`
                            : SPOT_FENCE_HEIGHT[fncHeight.toUpperCase()]}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="fence-height">
            <SvgRule />
            <div>
                <p className="snif-p mb-1 snif-semibold">Fencing height (at lowest point)</p>
                <p className="snif-p mb-3 deep-gray">{`${SPOT_FENCE_HEIGHT[fncHeight.toUpperCase()]} `}</p>
            </div>
        </div>
    );
};

const FencingPercent = ({ fencingPercentage }) => {
    if (fencingPercentage == null || fencingPercentage == 0) {
        return '';
    }

    return (
        <p className="percentage">
            <SvgAnaliticsBar width={16} height={16} className="icon" />
            <span className="snif-semibold">{fencingPercentage}% of guests</span> said fencing at this spot was secure enough to contain their dogs
        </p>
    );
};

const SmallDogPercent = ({ smallDogFencingPercentage }) => {
    if (smallDogFencingPercentage == null || smallDogFencingPercentage == 0) {
        return '';
    }

    return (
        <p className="percentage">
            <SvgSmallDog width={16} height={16} className="icon" />
            <span className="snif-semibold">{smallDogFencingPercentage}% of guests</span> with small dogs said fencing was secure.
        </p>
    );
};

const Gaps = ({ gaps, mobile }) => {
    if (mobile) {
        return (
            <div className="fence-height">
                <div className="d-flex">
                    <SvgFenceUnsafe />
                </div>
                <div>
                    <p className="snif-p mb-1 snif-semibold">Gaps or holes in fence</p>
                    <p className="snif-p mb-3 deep-gray">{gaps ? 'Gaps' : 'No gaps'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fence-gap mt-1">
            <p className="snif-s1 snif-semibold">
                Gaps or holes in fence: <span className="snif-regular">{gaps ? 'Gaps' : 'No gaps'}</span>
            </p>
        </div>
    );
};

const FencingTypes = ({ fencingType, mobile }) => {
    if (!fencingType?.length) {
        return '';
    }

    if (mobile) {
        return (
            <div className="fence-height">
                <div className="d-flex">
                    <SvgInfo />
                </div>
                <div>
                    <p className="snif-p mb-1 snif-semibold">Fencing type{fencingType.length > 1 ? 's' : ''}</p>
                    <p className="snif-p mb-3 deep-gray">
                        {fencingType.map((f, i) => {
                            const predef = FENCING_TYPES_FOR_EDIT.find((t) => t.value === f);
                            const type = predef ? predef.label : f;
                            return `${type}${fencingType.length - 1 !== i ? ', ' : ''}`;
                        })}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="fence-type mt-1">
            <p className="snif-s1 snif-semibold">
                Fencing type(s):{' '}
                <span className="snif-regular">
                    {fencingType.map((f, i) => {
                        const predef = FENCING_TYPES_FOR_EDIT.find((t) => t.value === f);
                        const type = predef ? predef.label : f;
                        return `${type}${fencingType.length - 1 !== i ? ', ' : ''}`;
                    })}
                </span>
            </p>
        </div>
    );
};

const FencingDetail = ({ fenceOpen, fencingDetail, setFenceOpen, Collapse, mobile }) => {
    if (!fencingDetail) {
        return '';
    }

    const isLong = fencingDetail.length > 240;
    const firstDesc = fencingDetail.substring(0, 240);

    if (mobile) {
        return (
            <div className="fence-height">
                <div className="d-flex">
                    <SvgMessageBubble />
                </div>
                <div>
                    <p className="snif-p mb-1 snif-semibold">Note from host</p>
                    <p className="snif-p mb-3 deep-gray break-word text-pre">
                        {isLong ? (
                            <>
                                <Collapse isOpen={fenceOpen}>{fenceOpen ? fencingDetail : firstDesc.padEnd(243, '.')}</Collapse>
                                <div onClick={() => setFenceOpen(!fenceOpen)} className="read-more snif-semibold text-dark mt-2 mb-2">
                                    {fenceOpen ? 'Show less details' : 'Show more details'}
                                    {fenceOpen ? (
                                        <SvgChevronUp width={14} className="ms-1 text-dark" />
                                    ) : (
                                        <SvgChevronDown width={14} className="ms-1 text-dark" />
                                    )}
                                </div>
                            </>
                        ) : (
                            fencingDetail
                        )}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="fence-detail break-word mt-1">
            <p className="snif-s1 snif-semibold">
                <span>Fencing details: </span>
                <span className="snif-regular text-pre">
                    {isLong ? (
                        <>
                            <Collapse isOpen={fenceOpen}>{fenceOpen ? fencingDetail : firstDesc.padEnd(243, '.')}</Collapse>
                            <div onClick={() => setFenceOpen(!fenceOpen)} className="read-more snif-semibold text-dark mt-2 mb-2">
                                {fenceOpen ? 'Show less details' : 'Show more details'}
                                {fenceOpen ? (
                                    <SvgChevronUp width={14} className="ms-1 text-dark" />
                                ) : (
                                    <SvgChevronDown width={14} className="ms-1 text-dark" />
                                )}
                            </div>
                        </>
                    ) : (
                        fencingDetail
                    )}
                </span>
            </p>
        </div>
    );
};

const ListingFencing = ({
    useStateSSR,
    fncHeight,
    encls,
    gaps,
    fencingType,
    fencingDetail,
    fencingPercentage,
    isSmallDogFenceTest,
    smallDogFencingPercentage,
}) => {
    const Collapse = useStateSSR ? Div : CollapseClient;
    const useStateC = useStateSSR || useState;
    const [fenceOpen, setFenceOpen] = useStateC(false);
    const haveInfo = encls || fncHeight || gaps || fencingType;
    const isFullyFenced = encls === ENCLOSURE_TYPE.FULLY_FENCED;
    const canShowPercent = (fencingPercentage && fencingPercentage > 0) || (smallDogFencingPercentage && smallDogFencingPercentage > 0);

    if (!haveInfo || !encls) {
        return '';
    }

    return (
        <div className="fencing px-2 px-md-0">
            <div className="snif-m1 snif-semibold mt-1 mb-3">Fencing</div>
            <div className="fence-details">
                <div>
                    <div className="d-md-none">
                        {canShowPercent && (
                            <div className="percentage-cont">
                                <FencingPercent {...{ fencingPercentage }} mobile />
                                {isSmallDogFenceTest && fencingPercentage > 0 && fencingPercentage > 0 && <p className="separator d-md-none" />}
                                {isSmallDogFenceTest && <SmallDogPercent {...{ smallDogFencingPercentage }} mobile />}
                            </div>
                        )}
                        <Enclosure {...{ encls }} mobile />
                        <FencingDetail {...{ fenceOpen, fencingDetail, setFenceOpen, Collapse }} mobile />
                        {isFullyFenced && <Fencing {...{ fncHeight }} mobile />}
                        <FencingTypes {...{ fencingType }} mobile />
                        <Gaps {...{ gaps }} mobile />
                    </div>
                    <div className="d-none d-md-block">
                        <Enclosure {...{ encls }} />
                        {isFullyFenced && <Fencing {...{ fncHeight }} />}
                    </div>
                </div>
                <div>
                    <div className="d-none d-md-block">
                        <FencingPercent {...{ fencingPercentage }} />
                        {isSmallDogFenceTest && <SmallDogPercent {...{ smallDogFencingPercentage }} />}
                        {isFullyFenced && (
                            <>
                                <FencingTypes {...{ fencingType }} />
                                <Gaps {...{ gaps }} />
                                <FencingDetail {...{ fenceOpen, fencingDetail, setFenceOpen, Collapse }} />
                            </>
                        )}
                    </div>
                </div>
            </div>
            <hr />
        </div>
    );
};

export default ListingFencing;
